import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';

// ========================================
// HTML Response Container - For legacy HTML rendering
// ========================================

const HtmlContainer = styled.div`
  width: 100%;
  min-height: 400px;
  
  /* Style injected HTML to match admin2 styling */
  & table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 4px;
  }
  
  & th, & td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34,36,38,.1);
  }
  
  & th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0,0,0,.87);
    border-top: none;
  }
  
  & tr:hover td {
    background: #f9fafb;
  }
  
  & a {
    color: #4183c4;
    text-decoration: none;
  }
  
  & a:hover {
    text-decoration: underline;
  }
  
  & .ui.form {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    align-items: flex-end;
  }
  
  & .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  & .ui.button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  
  & .ui.primary.button {
    background: #2185d0;
    color: #fff;
  }
  
  & .pagination {
    display: flex;
    gap: 4px;
    margin-top: 16px;
  }
`;

interface AuditEvent {
  id: string;
  actorId: string;
  actorName: string;
  eventType: string;
  createdAt: string;
  description: string;
}

interface SelectOption {
  value: string;
  label: string;
}

interface UserBasic {
  id: string;
  fullName: string;
  email: string;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
  background: white;
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-width: 140px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  ${({ $primary }) =>
    $primary
      ? 'background: #00a6c2; color: white; &:hover { background: #0094ab; }'
      : 'background: #e5e7eb; color: #374151; &:hover { background: #d1d5db; }'}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const EventLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: #6b7280;
`;

function AdminUserAuditEventsPage() {
  const { id } = useParams<{ id: string }>();
  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<UserBasic | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [actors, setActors] = useState<SelectOption[]>([]);
  const [eventTypes, setEventTypes] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  // Track if response was HTML (legacy audit service returns HTML)
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isHtmlResponse, setIsHtmlResponse] = useState(false);

  const [filters, setFilters] = useState({
    actor: '',
    from: '',
    to: '',
    eventType: ''
  });

  const fetchData = useCallback(async (filterParams?: typeof filters) => {
    setHtmlContent(null);
    setIsHtmlResponse(false);
    
    try {
      const params = filterParams || filters;
      const queryString = new URLSearchParams();
      if (params.actor) queryString.append('actor', params.actor);
      if (params.from) queryString.append('from', params.from);
      if (params.to) queryString.append('to', params.to);
      if (params.eventType) queryString.append('event_type', params.eventType);

      // Fetch user data
      const userRes = await api.get(`/admin2/users/${id}.json`);
      setUser({
        id: userRes.data.id,
        fullName: `${userRes.data.firstName || ''} ${userRes.data.lastName || ''}`.trim() || userRes.data.email,
        email: userRes.data.email
      });
      
      // Try to fetch audit events, handling both JSON and HTML responses
      try {
        const eventsRes = await api.get(`/admin2/users/${id}/audit_events.json?${queryString.toString()}`);
        
        // Check if we got HTML instead of JSON (service may return HTML)
        if (typeof eventsRes.data === 'string' && eventsRes.data.trim().startsWith('<')) {
          setIsHtmlResponse(true);
          setHtmlContent(eventsRes.data);
          setEvents([]);
        } else {
          setEvents(eventsRes.data.events || []);
          setActors(eventsRes.data.actors || []);
          setEventTypes(eventsRes.data.eventTypes || []);
        }
      } catch (jsonError) {
        // If JSON fails, try fetching as HTML (legacy audit service behavior)
        console.warn('JSON request failed, trying HTML fallback:', jsonError);
        
        const htmlResponse = await api.get(
          `/admin2/users/${id}/audit_events?${queryString.toString()}`,
          { 
            responseType: 'text',
            headers: { 'Accept': 'text/html' }
          }
        );
        
        if (typeof htmlResponse.data === 'string') {
          setIsHtmlResponse(true);
          // Extract body content if it's a full HTML page
          const bodyMatch = htmlResponse.data.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          const mainContent = htmlResponse.data.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
          const contentMatch = htmlResponse.data.match(/<div[^>]*class="[^"]*content[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
          
          setHtmlContent(
            mainContent?.[1] || 
            contentMatch?.[1] || 
            bodyMatch?.[1] || 
            htmlResponse.data
          );
          setEvents([]);
        }
      }
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id, filters]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleApplyFilters = () => {
    setLoading(true);
    fetchData(filters);
  };

  const handleReset = () => {
    const resetFilters = { actor: '', from: '', to: '', eventType: '' };
    setFilters(resetFilters);
    setLoading(true);
    fetchData(resetFilters);
  };

  if (loading && !user) {
    return (
      <AdminLayout title="Users" pageTitle="User Audit Events">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Users" pageTitle={`${user?.fullName || 'User'} - Audit Events`}>
      <Breadcrumb>
        <Link to="/admin2/users">Users</Link>
        <span>›</span>
        <Link to={`/admin2/users/${id}/edit`}>{user?.fullName} ({user?.email})</Link>
        <span>›</span>
        <span>Audit events</span>
      </Breadcrumb>

      <PageTitle>{user?.fullName}</PageTitle>

      <TabNav>
        <TabLink to={`/admin2/users/${id}/edit`}>User info</TabLink>
        <TabLink to={`/admin2/users/${id}/publications`}>Publications</TabLink>
        <TabLink to={`/admin2/users/${id}/stories`}>Stories</TabLink>
        <TabLink to={`/admin2/users/${id}/audit_events`} $active>Audit Events</TabLink>
        <TabLink to={`/admin2/users/${id}/content_origin_access`}>Content Origin Access</TabLink>
      </TabNav>

      <Section>
        <SectionTitle>Audit Events</SectionTitle>
        
        {isHtmlResponse && htmlContent ? (
          // Render HTML response from legacy audit service
          <HtmlContainer 
            ref={htmlContainerRef}
            dangerouslySetInnerHTML={{ __html: htmlContent }} 
          />
        ) : (
          <>
            <FiltersRow>
              <FilterGroup>
                <FilterLabel>Actor</FilterLabel>
                <Select value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })}>
                  <option value="">All</option>
                  {actors.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>From</FilterLabel>
                <DateInput
                  type="date"
                  value={filters.from}
                  onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>To</FilterLabel>
                <DateInput
                  type="date"
                  value={filters.to}
                  onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Event type</FilterLabel>
                <Select value={filters.eventType} onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}>
                  <option value="">All</option>
                  {eventTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
              </FilterGroup>

              <Button $primary onClick={handleApplyFilters}>Apply filters</Button>
              <Button onClick={handleReset}>Reset</Button>
            </FiltersRow>

            {events.length === 0 ? (
              <EmptyState>There are no audit events for this user.</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Event Type</Th>
                    <Th>Actor</Th>
                    <Th>Description</Th>
                    <Th>Date</Th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <Td>
                        <EventLink to={`/admin2/audit_events/${event.id}`}>{event.eventType}</EventLink>
                      </Td>
                      <Td>{event.actorName || 'System'}</Td>
                      <Td>{event.description}</Td>
                      <Td>{event.createdAt}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminUserAuditEventsPage;
